import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Select, { StylesConfig } from "react-select";
import PagePanel from "app/components/PagePanel";
import { useSelectedCoach } from "app/contexts/SelectedCoachContext";
import {
  schedulingFetchGet,
  schedulingFetchPatch,
} from "app/utils/request/scheduling/fetch";
import { Loading } from "app/components/Wrappers";
import { RootState } from "typedefs";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";
import CalendarSettingsLayout from "../Layout";
import styles from "./styles.module.scss";

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

type TimeFormat = "12h" | "24h";
type DateFormat = "us" | "eu";

type BookingPolicySettings = {
  calendarFirstDayOfWeek?: number;
  calendarTimeFormat?: TimeFormat;
  calendarDateFormat?: DateFormat;
};

type BookingPolicyResponse = {
  settings?: BookingPolicySettings;
};

type DayOption = { value: number; label: string };

const selectStyles: StylesConfig = {
  control: (base: any) => ({
    ...base,
    width: "100%",
    maxWidth: 300,
    minHeight: 44,
    borderRadius: 12,
    backgroundColor: "#fff",
    borderColor: "#ccc",
    boxShadow: "none",
    cursor: "pointer",
    ":hover": {
      borderColor: "#039be5",
    },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 12px" }),
  singleValue: (base: any) => ({ ...base, color: "#333", fontSize: 14 }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 14,
    cursor: "pointer",
    backgroundColor: state.isSelected
      ? "#039be5"
      : state.isFocused
        ? "#f5faff"
        : "#fff",
    color: state.isSelected ? "#fff" : "#333",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base: any) => ({ ...base, maxWidth: 300, zIndex: 20 }),
};

export default function CalendarDisplaySettings() {
  const { t } = useTranslation();
  const { selectedCoachId: contextCoachId } = useSelectedCoach();
  const userId = useSelector((state: RootState) => state.user.self?.id);
  const coachId = contextCoachId || userId;
  const { refresh } = useCalendarPreferences();

  const dayOptions: DayOption[] = useMemo(
    () =>
      DAYS_OF_WEEK.map((day) => ({ value: day.value, label: t(day.label) })),
    [t],
  );

  const [firstDay, setFirstDay] = useState<number>(0);
  const [timeFormat, setTimeFormat] = useState<TimeFormat>("12h");
  const [dateFormat, setDateFormat] = useState<DateFormat>("us");
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [initial, setInitial] = useState<{
    firstDay: number;
    timeFormat: TimeFormat;
    dateFormat: DateFormat;
  }>({
    firstDay: 0,
    timeFormat: "12h",
    dateFormat: "us",
  });

  useEffect(() => {
    if (!coachId) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    schedulingFetchGet<BookingPolicyResponse>(
      `coach_profiles/${coachId}/booking_policy`,
    )
      .then((data) => {
        if (cancelled) {
          return;
        }
        const settings = (data as BookingPolicyResponse)?.settings;
        if (!settings) {
          return;
        }
        const loaded = {
          firstDay:
            typeof settings.calendarFirstDayOfWeek === "number"
              ? settings.calendarFirstDayOfWeek
              : 0,
          timeFormat: settings.calendarTimeFormat || ("12h" as TimeFormat),
          dateFormat: settings.calendarDateFormat || ("us" as DateFormat),
        };
        setFirstDay(loaded.firstDay);
        setTimeFormat(loaded.timeFormat);
        setDateFormat(loaded.dateFormat);
        setInitial(loaded);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [coachId]);

  const hasChanges =
    firstDay !== initial.firstDay ||
    timeFormat !== initial.timeFormat ||
    dateFormat !== initial.dateFormat;

  const handleSave = async () => {
    if (!coachId || !hasChanges) {
      return;
    }
    setSaving(true);
    try {
      await schedulingFetchPatch(`coach_profiles/${coachId}/booking_policy`, {
        settings: {
          calendar_first_day_of_week: firstDay,
          calendar_time_format: timeFormat,
          calendar_date_format: dateFormat,
        },
      });
      await refresh();
      setInitial({ firstDay, timeFormat, dateFormat });
      toast.success(t("Calendar display settings updated."));
    } catch {
      toast.error(t("Failed to save. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <CalendarSettingsLayout>
        <div className={styles.container}>
          <Loading isLoading loadType="spinner" />
        </div>
      </CalendarSettingsLayout>
    );
  }

  return (
    <CalendarSettingsLayout>
      <div className={styles.container}>
        <PagePanel title={t("Calendar Display")}>
          <div className={styles.section}>
            <label className={styles.label}>{t("Start Week On")}</label>
            <p className={styles.description}>
              {t("Choose which day your calendar week begins on.")}
            </p>
            <div className={styles.select_wrapper}>
              <Select
                options={dayOptions}
                value={
                  dayOptions.find((opt) => opt.value === firstDay) ?? null
                }
                onChange={(opt: DayOption | null) => {
                  if (opt) {
                    setFirstDay(opt.value);
                  }
                }}
                isSearchable={false}
                styles={selectStyles}
              />
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.section}>
            <label className={styles.label}>{t("Time Format")}</label>
            <p className={styles.description}>
              {t("Choose how times are displayed on your calendar.")}
            </p>
            <div className={styles.toggle_group}>
              <button
                type="button"
                className={`${styles.toggle_button} ${timeFormat === "12h" ? styles.toggle_active : ""}`}
                onClick={() => setTimeFormat("12h")}
              >
                {t("12-hour")}
              </button>
              <button
                type="button"
                className={`${styles.toggle_button} ${timeFormat === "24h" ? styles.toggle_active : ""}`}
                onClick={() => setTimeFormat("24h")}
              >
                {t("24-hour")}
              </button>
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.section}>
            <label className={styles.label}>{t("Date Format")}</label>
            <p className={styles.description}>
              {t("Choose how dates are displayed across the application.")}
            </p>
            <div className={styles.toggle_group}>
              <button
                type="button"
                className={`${styles.toggle_button} ${styles.toggle_column} ${dateFormat === "us" ? styles.toggle_active : ""}`}
                onClick={() => setDateFormat("us")}
              >
                <span className={styles.toggle_label}>{t("US")}</span>
                <span className={styles.toggle_format}>
                  {t("(MM/DD/YYYY)")}
                </span>
              </button>
              <button
                type="button"
                className={`${styles.toggle_button} ${styles.toggle_column} ${dateFormat === "eu" ? styles.toggle_active : ""}`}
                onClick={() => setDateFormat("eu")}
              >
                <span className={styles.toggle_label}>{t("European")}</span>
                <span className={styles.toggle_format}>
                  {t("(DD/MM/YYYY)")}
                </span>
              </button>
            </div>
          </div>

          <div>
            <p className={styles.personal_note}>
              <i className="ico ico-info" />
              {t(
                "These settings only affect how your own calendar is displayed.",
              )}
            </p>
            <button
              type="button"
              className={styles.save_button}
              disabled={!hasChanges || saving}
              onClick={handleSave}
            >
              {t("Save Changes")}
            </button>
          </div>
        </PagePanel>
      </div>
    </CalendarSettingsLayout>
  );
}
